import Cookies from 'js-cookie';
import { clarity } from 'react-microsoft-clarity';

import type { Country } from '@skippay/core/@types/country';
import type { GA4Pageview } from '@skippay/core/components/GoogleAnalytics/types';
import { asNonEmptyString } from '@skippay/core/utils/string';

import {
	CLARITY_PROJECT_IDS,
	CLARITY_TRACKED_ROUTES,
	type ClarityEnv,
	type ClaritySurface,
} from 'constants/clarity';

import { promoSourceFromCookie } from './promoUtils';
import { isPaymentGatePathname, matchesRouteSegment, normalizePathname } from './routeUtils';

type ClarityAppType = 'web' | 'mob-app';

type ClarityPageview = Omit<GA4Pageview, 'event'>;

type ClarityState = {
	initialized: boolean;
	appType?: ClarityAppType;
	lastRouteDecision?: boolean;
	lastConsent?: boolean;
	consentPollId?: number;
};

let state: ClarityState = { initialized: false };

const isDev = process.env.TARGET_ENV !== 'prod';

function log(method: string, payload?: unknown): void {
	if (isDev) {
		// eslint-disable-next-line no-console
		console.log('[Clarity]', method, payload ?? '');
	}
}

function safeCall(call: () => void): void {
	try {
		call();
	} catch {
		// ignore
	}
}

export function resolveClarityProjectId(
	surface: ClaritySurface,
	country: Country | undefined,
	targetEnv: string | undefined
): string | undefined {
	const env: ClarityEnv = targetEnv === 'prod' ? 'prod' : 'accept';
	return asNonEmptyString(CLARITY_PROJECT_IDS[surface]?.[country as Country]?.[env]);
}

export function isClarityTrackedPath(pathname: string): boolean {
	const path = normalizePathname(pathname);
	return CLARITY_TRACKED_ROUTES.some((route) => matchesRouteSegment(path, route));
}

export function detectClaritySurface(pathname: string): ClaritySurface {
	const path = normalizePathname(pathname);

	if (isPaymentGatePathname(path)) {
		return 'paygate';
	}

	return 'selfcare';
}

// `mp_ck_set` ('A=1&P=0&R=0') is owned by packages/sdk-cookie/src/storage.ts
const CONSENT_COOKIE_NAME = 'mp_ck_set';

export function hasAnalyticsCookieConsent(): boolean {
	return /(^|&)A=1(&|$)/.test(Cookies.get(CONSENT_COOKIE_NAME) || '');
}

function applyConsent(granted: boolean): void {
	if (state.lastConsent === granted) {
		return;
	}
	state.lastConsent = granted;
	log('consent', granted);
	safeCall(() => clarity.consent(granted));
}

const CONSENT_POLL_INTERVAL_MS = 400;
const CONSENT_POLL_MAX_ATTEMPTS = 25;

function wireConsent(): void {
	state.lastConsent = false;
	if (hasAnalyticsCookieConsent()) {
		applyConsent(true);
	}

	let attempts = 0;
	state.consentPollId = window.setInterval(() => {
		const cookieSdk = window.skippay?.cookie;
		if (cookieSdk?.onConsentsChange) {
			window.clearInterval(state.consentPollId);
			applyConsent(Boolean(cookieSdk.getConsents?.()?.analytics));
			cookieSdk.onConsentsChange((consents) => applyConsent(Boolean(consents?.analytics)));
			return;
		}
		attempts += 1;
		if (attempts >= CONSENT_POLL_MAX_ATTEMPTS) {
			window.clearInterval(state.consentPollId);
			return;
		}
		applyConsent(hasAnalyticsCookieConsent());
	}, CONSENT_POLL_INTERVAL_MS);
}

type InitClarityOptions = {
	surface: ClaritySurface;
	country: Country | undefined;
};

export function clarityEvaluateRoute(pathname: string): void {
	if (typeof window === 'undefined' || !state.initialized) {
		return;
	}
	if (process.env.TARGET_ENV !== 'prod') {
		return;
	}
	const shouldTrack = isClarityTrackedPath(pathname);
	if (shouldTrack === state.lastRouteDecision) {
		return;
	}
	state.lastRouteDecision = shouldTrack;
	log(shouldTrack ? 'start' : 'stop', pathname);
	safeCall(() => (shouldTrack ? clarity.start() : clarity.stop()));
}

export function initClarity({ surface, country }: InitClarityOptions): void {
	if (typeof window === 'undefined' || state.initialized) {
		return;
	}
	if (process.env.NODE_ENV === 'development') {
		return;
	}

	const projectId = resolveClarityProjectId(surface, country, process.env.TARGET_ENV);

	if (!projectId) {
		log('skip', { reason: 'no project id', surface, country });
		return;
	}

	log('init', projectId);
	safeCall(() => clarity.init(projectId));

	if (!clarity.hasStarted()) {
		log('skip', { reason: 'clarity did not start' });
		return;
	}

	state.initialized = true;
	state.appType = surface === 'mobapp' ? 'mob-app' : 'web';

	clarityEvaluateRoute(window.location.pathname);

	if (surface === 'mobapp') {
		applyConsent(true);
	} else {
		wireConsent();
	}
}

function gaClientIdFromCookie(): string | undefined {
	const gaCookie = Cookies.get('_ga');
	return gaCookie ? gaCookie.split('.').slice(-2).join('.') : undefined;
}

export function buildClarityIdentity(pageview: ClarityPageview): {
	userId: string | undefined;
	tags: Record<string, string>;
} {
	const candidates: Record<string, unknown> = {
		app_type: state.appType,
		virtual_pw: pageview.pUrl ?? pageview.page_location,
		slug: pageview.pTitle ?? pageview.page_title,
		release: window.skippay?.version,
		client_id: gaClientIdFromCookie(),
		promo: promoSourceFromCookie(),
		transaction_id: pageview.pTransactionId,
	};

	const tags: Record<string, string> = {};
	Object.entries(candidates).forEach(([key, value]) => {
		const tagValue = asNonEmptyString(value);
		if (tagValue) {
			tags[key] = tagValue;
		}
	});

	return { userId: asNonEmptyString(pageview.userId), tags };
}

export function clarityHandlePageview(pageview: ClarityPageview): void {
	if (typeof window === 'undefined' || !state.initialized) {
		return;
	}

	const { userId, tags } = buildClarityIdentity(pageview);

	if (!userId) {
		return;
	}

	log('identify', userId);
	safeCall(() => clarity.identify(userId, {}));

	Object.entries(tags).forEach(([key, value]) => {
		safeCall(() => clarity.setTag(key, value));
	});
}

// eslint-disable-next-line no-underscore-dangle
export function __resetClarityForTests(): void {
	if (state.consentPollId !== undefined && typeof window !== 'undefined') {
		window.clearInterval(state.consentPollId);
	}
	state = { initialized: false };
}

const CLARITY_UNMASK_ATTR = 'data-clarity-unmask';
const CLARITY_MASK_ATTR = 'data-clarity-mask';

export function clarityMaskProps(shouldUnmask?: boolean): Record<string, 'true'> {
	return shouldUnmask ? { [CLARITY_UNMASK_ATTR]: 'true' } : { [CLARITY_MASK_ATTR]: 'true' };
}
